import { FC, useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  useColorModeValue,
  useToast,
  Badge,
  Spinner,
  Skeleton,
  SkeletonText,
} from '@chakra-ui/react';
import { MdArrowBack, MdCheckCircle } from 'react-icons/md';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Content,
  getContentComments,
  getContentQuestions,
  QuizData,
  markContentCompleted,
  updateQuizScore,
  getContentCompletion,
  getCourseEnrollmentId,
  getContentById,
  updateLastViewedContent,
  getCourseById,
  Course,
  Comment,
  Question
} from '../../services/university-service';
import { QuizContent, VideoContent, MarkdownContent } from '@bemap/university-microfrontends';
import Comments from './Comments';
import Questions from './Questions';
import { PageHeading } from '@bemap/ui';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@bemap/utils';

interface CoursePlayerProps {
  onBack: () => void;
  onContentComplete?: (contentId: number) => void;
  onQuizSubmit?: (contentId: number, answers: any) => void;
}

const CoursePlayer: FC<CoursePlayerProps> = ({
  onBack,
  onContentComplete,
  onQuizSubmit,
}) => {
  const { courseId, contentId } = useParams<{ courseId: string; contentId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [content, setContent] = useState<Content | null>(null);
  const [completionStatus, setCompletionStatus] = useState<any>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [timeSpent, setTimeSpent] = useState(0);
  const [isTrackingTime, setIsTrackingTime] = useState(false);
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [isQuizSubmitting, setIsQuizSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'comments' | 'questions'>('comments');
  const timeTrackingInterval = useRef<number | null>(null);

  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    if (courseId) {
      loadCourseAndContent();
    }
  }, [courseId, contentId]);

  useEffect(() => {
    return () => {
      stopTimeTracking();
    };
  }, [content?.id]);

  const loadCourseAndContent = async () => {
    if (!courseId || !user) return;
    setLoading(true);
    try {
      const [courseResponse, enrollmentResponse] = await Promise.all([
        getCourseById(parseInt(courseId)),
        getCourseEnrollmentId(parseInt(courseId), user.id)
      ]);

      if (courseResponse.statusCode !== 200) {
        throw new Error('Course not found');
      }

      if (enrollmentResponse.statusCode !== 200) {
        throw new Error('Course enrollment not found');
      }

      const courseData = courseResponse.message as Course;
      const enrollment = enrollmentResponse.message;
      let targetContentId = contentId;

      // If no contentId in URL, determine where to redirect
      if (!targetContentId) {
        if (enrollment.lastViewedContentId) {
          // Redirect to last viewed content
          targetContentId = enrollment.lastViewedContentId.toString();
        } else {
          // Redirect to first content of first module
          if (courseData.modules && courseData.modules.length > 0) {
            const firstModule = courseData.modules[0];
            if (firstModule.contents && firstModule.contents.length > 0) {
              targetContentId = firstModule.contents[0].id.toString();
            }
          }
        }

        // Navigate to the determined content
        if (targetContentId) {
          navigate(`/university/courses/${courseId}/content/${targetContentId}`, { replace: true });
          return;
        }
      }

      // Load the target content
      if (targetContentId) {
        await loadContent(parseInt(targetContentId));
      }
    } catch (error) {
      console.error('Error loading course and content:', error);
      toast({
        title: t('university.coursePlayer.error.loading_course.title'),
        description: t('university.coursePlayer.error.loading_course.description'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadContent = async (contentId: number) => {
    try {
      const response = await getContentById(contentId);
      if (response.statusCode === 200) {
        setContent(response.message);

        // Track last viewed content
        if (user && courseId) {
          try {
            await updateLastViewedContent(parseInt(courseId), user.id, contentId);
            await loadContentInformation();
          } catch (error) {
            console.error('Error updating last viewed content:', error);
          }
        }
      } else {
        toast({
          title: t('university.coursePlayer.error.content_not_found.title'),
          description: t('university.coursePlayer.error.content_not_found.description'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error loading content:', error);
      toast({
        title: t('university.coursePlayer.error.loading_content.title'),
        description: t('university.coursePlayer.error.loading_content.description'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const loadCommentsAndQuestions = async () => {
    if (!content) return;

    try {

      // Load comments and questions concurrently using Promise.all
      const [commentsResponse, questionsResponse] = await Promise.all([
        getContentComments(content.id),
        getContentQuestions(content.id)
      ]);

      // Handle comments response
      if (commentsResponse.statusCode === 200) {
        setComments(commentsResponse.message || []);
      }

      // Handle questions response
      if (questionsResponse.statusCode === 200) {
        setQuestions(questionsResponse.message || []);
      }
    } catch (error) {
      console.error('Error loading comments and questions:', error);
      toast({
        title: t('university.coursePlayer.error.loading_data.title'),
        description: t('university.coursePlayer.error.loading_data.description'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const loadCompletionStatus = async () => {
    if (!content || !user || !courseId) return;

    try {
      // Get course enrollment ID first
      const enrollmentResponse = await getCourseEnrollmentId(parseInt(courseId), user.id);
      if (enrollmentResponse.statusCode === 200) {
        const courseStudentId = enrollmentResponse.message.id;

        // Get content completion status
        const completionResponse = await getContentCompletion(courseStudentId, content.id);
        if (completionResponse.statusCode === 200) {
          setCompletionStatus(completionResponse.message);
        }
      }
    } catch (error) {
      console.error('Error loading completion status:', error);
    }
  };

  const loadContentInformation = async () => {
    await Promise.all([
      loadCommentsAndQuestions(),
      loadCompletionStatus(),
      startTimeTracking()
    ]);
  }

  const startTimeTracking = () => {
    if (!content || isTrackingTime) return;

    setIsTrackingTime(true);
    timeTrackingInterval.current = setInterval(() => {
      setTimeSpent(prev => prev + 1);
    }, 1000);
  };

  const stopTimeTracking = () => {
    if (timeTrackingInterval.current) {
      clearInterval(timeTrackingInterval.current);
      timeTrackingInterval.current = null;
    }
    setIsTrackingTime(false);
  };

  const handleContentComplete = async () => {
    if (!content || !user || isMarkingComplete || !courseId) return;

    try {
      setIsMarkingComplete(true);
      stopTimeTracking();

      // Get course enrollment ID
      const enrollmentResponse = await getCourseEnrollmentId(parseInt(courseId), user.id);
      if (enrollmentResponse.statusCode !== 200) {
        throw new Error('Course enrollment not found');
      }

      const courseStudentId = enrollmentResponse.message.id;

      // Mark content as completed
      const response = await markContentCompleted(
        courseStudentId,
        content.id,
        content.moduleId,
        content.type,
        undefined, // score
        undefined, // quizAnswers
        timeSpent
      );

      if (response.statusCode === 201) {
        setCompletionStatus(response.message);

        // Update local state
        if (onContentComplete) {
          onContentComplete(content.id);
        }

        toast({
          title: t('university.coursePlayer.success.content_completed.title'),
          description: t('university.coursePlayer.success.content_completed.description', { title: content.title }),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      console.error('Error marking content as completed:', error);
      toast({
        title: t('university.coursePlayer.error.completion_failed.title'),
        description: error.message || t('university.coursePlayer.error.completion_failed.description'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const handleQuizSubmit = async (answers: any) => {
    if (!content || !user || !courseId) return;

    try {
      setIsQuizSubmitting(true);
      setShowQuizResults(false);

      // Get course enrollment ID
      const enrollmentResponse = await getCourseEnrollmentId(parseInt(courseId), user.id);
      if (enrollmentResponse.statusCode !== 200) {
        throw new Error('Course enrollment not found');
      }

      const courseStudentId = enrollmentResponse.message.id;

      // Update quiz score (backend will calculate the score)
      const response = await updateQuizScore(
        courseStudentId,
        content.id,
        answers
      );

      const completion = response.message;
      setCompletionStatus(completion);

      // Show quiz result
      const score = completion.score || 0;
      const passed = completion.isCompleted || false;

      toast({
        title: t('university.coursePlayer.quiz.submitted.title'),
        description: t('university.coursePlayer.quiz.submitted.description', {
          score: score.toFixed(1),
          passed: passed ? '✅' : '❌'
        }),
        status: passed ? 'success' : 'warning',
        duration: 5000,
        isClosable: true,
      });

      // Mark as completed if passed
      if (passed) {
        await handleContentComplete();
      }

      // Call parent handler
      if (onQuizSubmit) {
        onQuizSubmit(content.id, { ...answers, score, passed });
      }

      // Show results
      setShowQuizResults(true);
    } catch (error: any) {
      console.error('Error submitting quiz:', error);
      toast({
        title: t('university.coursePlayer.error.quiz_submission_failed.title'),
        description: error.message || t('university.coursePlayer.error.quiz_submission_failed.description'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsQuizSubmitting(false);
    }
  };



  const loadComments = async () => {
    if (!content) return;

    try {
      const response = await getContentComments(content.id);
      if (response.statusCode === 200) {
        setComments(response.message || []);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const loadQuestions = async () => {
    if (!content) return;

    try {
      const response = await getContentQuestions(content.id);
      if (response.statusCode === 200) {
        setQuestions(response.message || []);
      }
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  };

  const renderContent = () => {
    if (!content) {
      return (
        <Box
          bg="gray.700"
          w="100%"
          h="360px"
          borderRadius="lg"
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="whiteAlpha.700"
        >
          {t('university.coursePlayer.select_content_to_start')}
        </Box>
      );
    }

    // Convert content to the format expected by microfrontend components
    const baseContentItem = {
      id: content.id.toString(),
      title: content.title,
      type: content.type,
      duration: content.duration,
      completed: completionStatus?.isCompleted || content.completed,
      active: content.active,
    };

    switch (content.type) {
      case 'video':
        return (
          <Box position="relative">
            <VideoContent
              content={{ ...baseContentItem, data: content.data }}
            />
          </Box>
        );
      case 'document':
        return (
          <Box position="relative">
            <MarkdownContent
              content={{ ...baseContentItem, data: content.data }}
              onComplete={() => {
                handleContentComplete();
              }}
            />
          </Box>
        );
      case 'quiz':
        return (
          <Box position="relative">
            <QuizContent
              content={{ ...baseContentItem, data: content.data as QuizData }}
              score={completionStatus?.score || undefined}
              passed={completionStatus?.isCompleted || undefined}
              isSubmitting={isQuizSubmitting}
              showResults={showQuizResults}
              t={t}
              onSubmit={(_contentId: string, answers: Record<string, any>) => {
                handleQuizSubmit(answers);
              }}
            />
          </Box>
        );
      default:
        return (
          <Box
            bg="gray.700"
            w="100%"
            h="360px"
            borderRadius="lg"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="whiteAlpha.700"
          >
            {t('university.coursePlayer.unsupported_content_type')}
          </Box>
        );
    }
  };

  const VideoPlayerSkeleton = () => (
    <VStack align="stretch" spacing={6}>
      {/* Header Skeleton */}
      <VStack align="stretch" spacing={2}>
        <Skeleton height="40px" width="300px" />
        <HStack spacing={4}>
          <Skeleton height="24px" width="100px" />
          <Skeleton height="20px" width="80px" />
        </HStack>
      </VStack>

      {/* Video Player Skeleton */}
      <Box position="relative" bg="gray.300" borderRadius="lg" overflow="hidden">
        <Box
          position="relative"
          width="100%"
          paddingBottom="56.25%"
        >
          {/* Video placeholder */}
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Spinner size="xl" />
          </Box>

          {/* Video title overlay */}
          <Box
            position="absolute"
            top={4}
            left={4}
            right={4}
            zIndex={2}
          >
            <SkeletonText noOfLines={2} spacing={2} />
          </Box>
        </Box>
      </Box>

      {/* Tabs Skeleton */}
      <Box>
        <HStack spacing={0} borderBottom="1px" borderColor="gray.200" mb={4}>
          <Skeleton height="40px" width="120px" />
          <Skeleton height="40px" width="120px" />
        </HStack>

        {/* Comments/Questions Content Skeleton */}
        <VStack align="stretch" spacing={4}>
          <Skeleton height="24px" width="200px" />
          <Skeleton height="20px" width="300px" />
          <Skeleton height="100px" width="100%" />
          <HStack justify="flex-end">
            <Skeleton height="36px" width="100px" />
          </HStack>

          {/* Comment items skeleton */}
          <VStack align="stretch" spacing={3}>
            {[1, 2, 3].map((i) => (
              <Box key={i} p={3} border="1px" borderColor="gray.200" borderRadius="md">
                <HStack spacing={3} mb={2}>
                  <Skeleton width="32px" height="32px" borderRadius="full" />
                  <VStack align="start" spacing={1} flex={1}>
                    <Skeleton height="16px" width="120px" />
                    <Skeleton height="12px" width="80px" />
                  </VStack>
                </HStack>
                <SkeletonText noOfLines={2} spacing={2} />
              </Box>
            ))}
          </VStack>
        </VStack>
      </Box>
    </VStack>
  );

  if (loading) {
    return <VideoPlayerSkeleton />;
  }

  return (
    <VStack align="stretch" spacing={6}>
      <VStack align="stretch" spacing={2}>
        <PageHeading title={content?.title || ''} actions={[
          {
            label: t('university.courses.back_to_courses'),
            onClick: onBack,
            variant: 'outline',
            icon: <Icon as={MdArrowBack} />,
          },
          {
            label: completionStatus?.isCompleted ? t('university.coursePlayer.mark_as_complete.completed') : t('university.coursePlayer.mark_as_complete.in_progress'),
            onClick: handleContentComplete,
            variant: 'solid',
            icon: <Icon as={MdCheckCircle} />,
            isLoading: isMarkingComplete,
          }
        ]} />
        {content && (
          <HStack spacing={4}>
            <Badge colorScheme={completionStatus?.isCompleted ? 'green' : 'blue'}>
              {completionStatus?.isCompleted ? t('university.coursePlayer.status.completed') : t('university.coursePlayer.status.in_progress')}
            </Badge>
            <Text fontSize="sm" color="gray.500">
              {content.duration}
            </Text>
            {/* {timeSpent > 0 && (
              <Text fontSize="sm" color="gray.500">
                {t('university.coursePlayer.time_spent', { seconds: timeSpent })}
              </Text>
            )} */}
          </HStack>
        )}
      </VStack>

      {/* Content Player */}
      {renderContent()}

      {/* Comments and Questions Tabs */}
      {content && (
        <Box>
          <HStack spacing={0} borderBottom="1px" borderColor={borderColor} mb={4}>
            <Button
              variant={activeTab === 'comments' ? 'solid' : 'ghost'}
              colorScheme={activeTab === 'comments' ? 'blue' : 'gray'}
              borderRadius="none"
              borderBottom={activeTab === 'comments' ? '2px solid' : 'none'}
              borderColor="blue.500"
              onClick={() => setActiveTab('comments')}
            >
              {t('university.coursePlayer.tabs.comments', { count: comments.length })}
            </Button>
            <Button
              variant={activeTab === 'questions' ? 'solid' : 'ghost'}
              colorScheme={activeTab === 'questions' ? 'blue' : 'gray'}
              borderRadius="none"
              borderBottom={activeTab === 'questions' ? '2px solid' : 'none'}
              borderColor="blue.500"
              onClick={() => setActiveTab('questions')}
            >
              {t('university.coursePlayer.tabs.questions', { count: questions.length })}
            </Button>
          </HStack>

          {activeTab === 'comments' ? (
            <Comments
              comments={comments}
              contentId={content.id}
              onCommentAdded={loadComments}
            />
          ) : (
            <Questions
              questions={questions}
              contentId={content.id}
              onQuestionAdded={loadQuestions}
            />
          )}
        </Box>
      )}
    </VStack>
  );
};

export default CoursePlayer; 